import { describeDaysLeft, itemEmoji } from "@larder/shared";
import type { TrackedItem } from "../data/items";

/**
 * The check-in card. This is the whole product in one control.
 *
 * THREE buttons, not two. "Ate it" versus "Threw it" is what powers the streak,
 * the red calendar cells and the waste rupees; collapsing them into "done"
 * would throw away the only signal that distinguishes success from failure.
 * "Still here" is the third because the honest answer is often that our date
 * was simply wrong — and that answer is what teaches the defaults.
 *
 * See docs/product/item-lifecycle.md.
 */
export function CheckIn({
  item,
  onAnswer,
  busy,
  leaving,
  index = 0,
}: {
  item: TrackedItem;
  onAnswer: (type: "ate" | "tossed" | "still_here") => void;
  busy: boolean;
  /** Playing its exit animation; the write has not been committed yet. */
  leaving?: boolean;
  index?: number;
}) {
  const days = item.derived.daysLeft ?? 0;

  return (
    <div
      className={`checkin${leaving ? " leaving" : ""}`}
      // The rail down the left and the glyph tile take their colour from this:
      // a card that is already past its date should not look the same as one
      // that has two days left, when the whole point of the card is the date.
      data-urgency={days < 0 ? "past" : "soon"}
      // Short stagger so the eye follows the list down rather than being hit
      // with everything at once. Capped, or a long list feels sluggish.
      style={{ animationDelay: `${Math.min(index, 6) * 45}ms` }}
      data-testid={`checkin-${item.name}`}
    >
      <div className="checkin-head">
        {/* Decorative, like the one on a list row: the name sits right beside
            it, so announcing "milk carton emoji" adds nothing. */}
        <span className="checkin-glyph" aria-hidden="true">
          {itemEmoji(item.emoji, item.category)}
        </span>
        <p className="checkin-title">
          <strong>{item.name}</strong>{" "}
          <span className="dim">
            {days < 0
              ? `was due ${describeDaysLeft(days)}`
              : `expires ${describeDaysLeft(days)}`}
          </span>
        </p>
      </div>
      <div className="checkin-actions">
        {/* `data-cue` and nothing else: the feedback layer is delegated, so
            three buttons that sound different from each other cost three
            attributes rather than three handlers. Naming a cue also replaces
            the generic press tick these would otherwise get for being
            `.answer`, which is what stops one press making two noises.
            See ui/cues.ts. */}
        <button
          type="button"
          className="answer good"
          disabled={busy || leaving}
          onClick={() => onAnswer("ate")}
          data-cue="ate"
          data-testid="answer-ate"
        >
          Ate it
        </button>
        <button
          type="button"
          className="answer bad"
          disabled={busy || leaving}
          onClick={() => onAnswer("tossed")}
          data-cue="tossed"
          data-testid="answer-tossed"
        >
          Threw it
        </button>
        <button
          type="button"
          className="answer neutral"
          disabled={busy || leaving}
          onClick={() => onAnswer("still_here")}
          data-cue="still"
          data-testid="answer-still-here"
        >
          Still here
        </button>
      </div>
    </div>
  );
}
